/**
 * Where the admin UI learns which issuer to log in against.
 *
 * Resolution order, first hit wins:
 *  1. `GET /api/v1/auth/config` on the API server. The API server takes its
 *     issuer from `OpenDeskCluster.status.controlPlaneAuth`, so this is the
 *     issuer it validates tokens for. Both ends then agree by construction
 *     (D171, D165).
 *  2. `/config.js`, rendered by the frontend's Helm chart into
 *     `window.__OPENDESK_CONFIG__`.
 *  3. The build-time Vite env vars (npm run dev).
 *  4. The defaults.
 *
 * The CSP is still sent by nginx from the Helm value `frontend.keycloakOrigin`.
 * When the API publishes an issuer on another origin, the browser blocks the
 * login and the console shows the warning below.
 */

export interface AuthConfig {
  keycloakUrl: string;
  clientId: string;
  source: "api" | "runtime" | "env" | "default";
}

/** Shape the deployment injects via `/config.js`. */
export interface RuntimeConfig {
  keycloakUrl?: string;
  clientId?: string;
}

export const DEFAULT_KEYCLOAK_URL = "https://keycloak.mgmt.dev-ai.wisbric.com/realms/opendesk";
export const DEFAULT_CLIENT_ID = "opendesk-operator-ui";

/** Path the API server publishes its resolved login configuration on. */
export const AUTH_CONFIG_PATH = "/api/v1/auth/config";

/** How long the UI waits for the API server before it falls back. */
const AUTH_CONFIG_TIMEOUT_MS = 3000;

export function runtimeConfig(): RuntimeConfig {
  if (typeof window === "undefined") return {};
  return (window as { __OPENDESK_CONFIG__?: RuntimeConfig }).__OPENDESK_CONFIG__ ?? {};
}

/** Steps 2 to 4: everything that needs no network round trip. */
export function fallbackAuthConfig(): AuthConfig {
  const rt = runtimeConfig();
  if (rt.keycloakUrl) {
    return { keycloakUrl: rt.keycloakUrl, clientId: rt.clientId ?? DEFAULT_CLIENT_ID, source: "runtime" };
  }
  const envUrl = import.meta.env.VITE_KEYCLOAK_URL;
  if (envUrl) {
    return {
      keycloakUrl: envUrl,
      clientId: import.meta.env.VITE_KEYCLOAK_CLIENT_ID ?? DEFAULT_CLIENT_ID,
      source: "env",
    };
  }
  return { keycloakUrl: DEFAULT_KEYCLOAK_URL, clientId: DEFAULT_CLIENT_ID, source: "default" };
}

interface AuthConfigEnvelope {
  data?: { issuerUrl?: string; clientId?: string };
}

function originOf(url: string): string {
  try {
    return new URL(url).origin;
  } catch {
    return "";
  }
}

/**
 * Step 1 with the fallbacks behind it. Never throws: a login page that
 * cannot render because the API server is down is worse than one that logs
 * in against the Helm-configured issuer.
 */
export async function resolveAuthConfig(
  fetchImpl: typeof fetch = globalThis.fetch,
): Promise<AuthConfig> {
  const fallback = fallbackAuthConfig();
  if (typeof fetchImpl !== "function") return fallback;

  try {
    const res = await fetchImpl(AUTH_CONFIG_PATH, {
      cache: "no-store",
      signal: AbortSignal.timeout(AUTH_CONFIG_TIMEOUT_MS),
    });
    if (!res.ok) return fallback;
    const body = (await res.json()) as AuthConfigEnvelope;
    const issuer = body.data?.issuerUrl?.trim();
    if (!issuer) return fallback;

    const resolved: AuthConfig = {
      keycloakUrl: issuer,
      clientId: body.data?.clientId?.trim() || fallback.clientId,
      source: "api",
    };
    const cspOrigin = originOf(fallback.keycloakUrl);
    if (fallback.source === "runtime" && cspOrigin && cspOrigin !== originOf(issuer)) {
      console.warn(
        `Control-plane issuer ${issuer} from the API server is on another origin than ` +
          `${fallback.keycloakUrl} from /config.js. The CSP names the latter; set ` +
          `frontend.keycloakOrigin in the Helm values to the issuer origin.`,
      );
    }
    return resolved;
  } catch {
    return fallback;
  }
}
